'use client'

import { useQuery } from '@tanstack/react-query'

import {
  getDistrictsByRegency,
  getPostalCodeByVillageId,
  getProvinces,
  getRegenciesByProvince,
  getVillagesByDistrict,
} from '@helper/utils/domain'

export function useAddressProvinces({ country }: { country?: string }) {
  const response = useQuery({
    queryKey: [`province-data-${country}`],
    queryFn: () => {
      if (!country) throw new Error('Country is required')

      const countryId = country

      return getProvinces(countryId)
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!country,
  })

  return response
}

export function useAddressRegencies({ province, country }: { province?: string; country?: string }) {
  const response = useQuery({
    queryKey: [`regencies-data-${province}-${country}`],
    queryFn: () => {
      if (!province || !country) throw new Error('Province and country are required')

      const countryId = country

      const provinces = getProvinces(countryId)

      const provinceId = provinces.find((item) => item.name === province)?.id

      if (!provinceId) throw new Error('Province not found')

      const response = getRegenciesByProvince(provinceId)

      return response
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!province,
  })

  return response
}

export function useAddressDistricts({
  regency,
  province,
  country,
}: {
  regency?: string
  province?: string
  country?: string
}) {
  const response = useQuery({
    queryKey: [`district-data-${regency}-${province}-${country}`],
    queryFn: () => {
      if (!regency || !province || !country) throw new Error('Regency, province, and country are required')

      const countryId = country

      const provinces = getProvinces(countryId)

      const provinceId = provinces.find((item) => item.name === province)?.id

      if (!provinceId) throw new Error('Province not found')

      const regencies = getRegenciesByProvince(provinceId)

      const regencyId = regencies.find((item) => item.name === regency)?.id

      if (!regencyId) throw new Error('Regency not found')

      const response = getDistrictsByRegency(regencyId)

      return response
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!regency && !!province,
  })

  return response
}

export function useAddressVillages({
  district,
  regency,
  province,
  country,
}: {
  district?: string
  regency?: string
  province?: string
  country?: string
}) {
  const response = useQuery({
    queryKey: [`village-data-${district}-${regency}-${province}`],
    queryFn: async () => {
      if (!district || !regency || !province || !country) {
        throw new Error('District, regency, province, and country are required')
      }

      const countryId = country

      const provinces = getProvinces(countryId)

      const provinceId = provinces.find((item) => item.name === province)?.id

      if (!provinceId) throw new Error('Province not found')

      const regencies = getRegenciesByProvince(provinceId)

      const regencyId = regencies.find((item) => item.name === regency)?.id

      if (!regencyId) throw new Error('Regency not found')

      const districts = getDistrictsByRegency(regencyId)

      const districtId = districts.find((item) => item.name === district)?.id

      if (!districtId) throw new Error('District not found')

      const response = getVillagesByDistrict(districtId)

      // Attaching the postal code here lets the address form fill it in the moment a village is
      // picked. Villages missing from the postal code dataset get null and stay manual.
      return await Promise.all(
        response.map(async (village) => ({
          ...village,
          postalCode: await getPostalCodeByVillageId(village.id),
        })),
      )
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    enabled: !!district && !!regency && !!province,
  })

  return response
}
